import { apiFetch } from './client.ts';
import type { Singer, StrapiListResponse } from './types.ts';

/**
 * KJ-side singer lookups/creation on the plain `singer` core CRUD (already granted to the `kj`
 * role — strapi-template/src/bootstrap/roles.js). Server-side dedup uses `nameNorm` (NFKC-folded,
 * private — strapi-template/src/api/singer/content-types/singer/lifecycles.js), which this client
 * never computes itself; see `findOrCreateSinger`'s own comment for the approximation used here
 * instead.
 */

/** How many recent singers the picker opens with. A venue's working set on any given night, not
 * its whole history — see `searchSingers` for how the rest is reached. */
export const SINGER_PAGE_SIZE = 100;
/** A typed search is a shortlist, not a browse: the picker shows eight rows. */
export const SINGER_SEARCH_SIZE = 20;

/** `GET /api/singers`, most-recently-seen first — feeds `SingerPicker`'s "existing singers" list. */
export async function listSingers(): Promise<Singer[]> {
  const res = await apiFetch<StrapiListResponse<Singer>>(
    `/api/singers?sort=lastSeenAt:desc&pagination[pageSize]=${SINGER_PAGE_SIZE}`
  );
  return res.data;
}

/**
 * MVP-452. `listSingers()` is one page of 100 and nothing said so. A venue with more than 100
 * regulars is precisely the venue the regular-singer feature exists for (SPEC AC-Q-5), and because
 * that page is sorted `lastSeenAt:desc`, the singers it drops are the ones who have been away
 * longest — the assumption fails at the exact moment a regular comes back after two months, which
 * is the moment a KJ most wants to be recognised.
 *
 * Server-side search rather than a second page or an infinite scroll: the picker renders eight
 * rows. Scrolling a pool the KJ cannot see is not the question they are asking — they are typing a
 * name at somebody standing in front of them, and the answer has to come from the whole directory,
 * not from whichever hundred happened to be cached. The first open is untouched and stays one
 * request, which is the other half of that promise.
 *
 * `$containsi` matches the client-side `includes()` filter it replaces, so a KJ who types three
 * letters in the middle of a name gets what they have always got, over every singer instead of 100.
 */
export async function searchSingers(query: string, signal?: AbortSignal): Promise<Singer[]> {
  const q = query.trim();
  if (!q) return [];
  const params = [
    `filters[name][$containsi]=${encodeURIComponent(q)}`,
    'sort=lastSeenAt:desc',
    `pagination[pageSize]=${SINGER_SEARCH_SIZE}`,
  ].join('&');
  const res = await apiFetch<StrapiListResponse<Singer>>(`/api/singers?${params}`, { signal });
  return res.data;
}

/**
 * `PUT /api/singers/:documentId` — the regular-singer heart (SPEC AC-Q-5). Core CRUD, already
 * granted to `kj` (`crudActions('singer')` in roles.js), so this needs no bespoke route.
 *
 * Writing the flag is the ONLY thing the client does here. Everything AC-Q-5 promises after that
 * is the server's: `singer/content-types/singer/lifecycles.js` snapshots the queue the singer
 * already has the moment the flag goes on, `queue-entry`'s lifecycle keeps that snapshot current
 * through every later add/remove/move/key-change (including undo/redo, which never re-enters the
 * queue service), and `rotation.js`'s `addSinger()` replays it into the next session. A client
 * that tried to maintain the list itself would be wrong the first time two cockpits were open.
 *
 * Turning it OFF deliberately does not clear the remembered list — see `regularQueueSnapshot`'s
 * own description in the schema.
 */
export async function setSingerRegular(documentId: string, isRegular: boolean): Promise<Singer> {
  const res = await apiFetch<{ data: Singer }>(`/api/singers/${encodeURIComponent(documentId)}`, {
    method: 'PUT',
    body: { data: { isRegular } },
    retry: false,
  });
  return res.data;
}

/**
 * Reuses an existing singer by name (case-insensitive) or creates a new one — the same "stack
 * onto your own singer rather than fork a duplicate" behaviour
 * `song-request/services/song-request.js`'s own `findOrCreateSinger` gives a guest request.
 *
 * ponytail: the server's uniqueness key is `nameNorm`, an NFKC fold (`utils/text-norm.js`) this
 * client does not have access to (it is `private`, never returned by `GET /api/singers`). A
 * client-side `filters[name][$eqi]` case-insensitive match approximates it — good enough to catch
 * the common "same name, different case" duplicate without re-implementing Unicode folding here;
 * an accent/width variant can still slip through and land on the server's own uniqueness check
 * instead (a 400, surfaced as a normal `ApiError`).
 */
export async function findOrCreateSinger(name: string): Promise<Singer> {
  const trimmed = name.trim();
  const query = `filters[name][$eqi]=${encodeURIComponent(trimmed)}&pagination[pageSize]=1`;
  const existing = await apiFetch<StrapiListResponse<Singer>>(`/api/singers?${query}`);
  if (existing.data[0]) return existing.data[0];

  const now = new Date().toISOString();
  const res = await apiFetch<{ data: Singer }>('/api/singers', {
    method: 'POST',
    body: { data: { name: trimmed, firstSeenAt: now, lastSeenAt: now } },
    retry: false,
  });
  return res.data;
}
